import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { Deckblatt, useDeckblatt } from "./Deckblatt";
import { KundeFormular, type KundenArt } from "./KundeFormular";
import type { Kunde } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Kunde anlegen — im Deckblatt, ohne die Maske zu verlassen.

   Das ist der Baustein, den die Schnellerfassung, das Angebot und die
   Kundenliste einhängen. Er ist dünn, und das ist Absicht: Das Blatt
   kommt aus `Deckblatt`, das Formular aus `KundeFormular`. Hier steht
   nur, wie beides zusammengeht — Titel, Untertitel, und dass der
   Umschalter Kunde/Interessent hier im Zustand lebt und nicht im Weg
   (auf `/kunden/neu` steht er in der Adresse, weil man den Link
   weitergeben kann; ein Blatt hat keine Adresse).

   Einhängen, in drei Zeilen:

     const [anlegen, setzeAnlegen] = useState(false);
     …
     <KundeAnlegen
       offen={anlegen}
       schliessen={() => setzeAnlegen(false)}
       angelegt={(k) => { setzeAnlegen(false); setzeKunde(k); }}
     />

   `angelegt` bekommt den fertigen Kunden mit Nummer. Was der Aufrufer
   damit tut, ist seine Sache — in der Schnellerfassung steht er danach
   gewählt im Feld, in der Liste steht er als neue Zeile.
   ══════════════════════════════════════════════════════════════════ */

/** Das Formular im Blatt: Der Knopf „Abbrechen" geht durch das Blatt,
 *  damit die Nachfrage bei angefangener Eingabe auch dort greift. */
function ImBlatt({
  art, artWechseln, firma, angelegt, schmutzigAendern,
}: {
  art: KundenArt;
  artWechseln: (neu: KundenArt) => void;
  firma?: string;
  angelegt: (kunde: Kunde) => void;
  schmutzigAendern: (schmutzig: boolean) => void;
}) {
  const { schliessen } = useDeckblatt();
  return (
    <KundeFormular
      form="blatt"
      art={art}
      artWechseln={artWechseln}
      abbrechen={schliessen}
      angelegt={angelegt}
      {...(firma ? { vorbelegt: { firma } } : {})}
      schmutzigAendern={schmutzigAendern}
    />
  );
}

export function KundeAnlegen({
  offen, schliessen, angelegt, art = "kunde", firma,
}: {
  offen: boolean;
  schliessen: () => void;
  angelegt: (kunde: Kunde) => void;
  /** Womit das Blatt aufgeht — der Anwender kann drinnen umschalten. */
  art?: KundenArt;
  /** Der Name, der schon in der Suche stand. */
  firma?: string;
}) {
  const t = useT();
  const [artJetzt, setzeArtJetzt] = useState<KundenArt>(art);
  const [schmutzig, setzeSchmutzig] = useState(false);

  // Beim Öffnen gilt wieder, was der Aufrufer vorgibt — nicht, was beim
  // letzten Mal zuletzt gewählt war.
  useEffect(() => {
    if (offen) { setzeArtJetzt(art); setzeSchmutzig(false); }
  }, [offen, art]);

  const interessent = artJetzt === "interessent";

  return (
    <Deckblatt
      offen={offen}
      schliessen={schliessen}
      schmutzig={schmutzig}
      titel={t(interessent ? "kundeNeu.titelInteressent" : "kundeNeu.titelKunde")}
      untertitel={t(interessent ? "kundeNeu.unterInteressent" : "kundeNeu.unterKunde")}
    >
      <ImBlatt
        art={artJetzt}
        artWechseln={setzeArtJetzt}
        {...(firma ? { firma } : {})}
        angelegt={angelegt}
        schmutzigAendern={setzeSchmutzig}
      />
    </Deckblatt>
  );
}
